import fromUnixTime from 'date-fns/fromUnixTime';
import differenceInDays from 'date-fns/differenceInDays';
import Cookies from 'js-cookie';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { SESSION_STORAGE_KEYS } from 'dashboard/constants/sessionStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import SessionStorage from 'shared/helpers/sessionStorage';
import { isLocalAuthPreview } from 'shared/helpers/localAuthPreview';
import { emitter } from 'shared/helpers/mitt';
import {
  ANALYTICS_IDENTITY,
  ANALYTICS_RESET,
  CHATWOOT_RESET,
  CHATWOOT_SET_USER,
} from '../../constants/appEvents';

const SESSION_COOKIE_KEYS = ['cw_d_session_info', 'auth_data', 'user'];
// Login at /app/login/agent without path=/ scopes the cookie to /app/login,
// so /app/accounts/... never receives it and the agent bounces back to login.
const STALE_SESSION_COOKIE_PATHS = [
  '/app/login',
  '/app/login/',
  '/app/login/agent',
  '/app/login/agent/',
];
const AUTH_HEADER_KEYS = [
  'access-token',
  'token-type',
  'client',
  'expiry',
  'uid',
];

const sessionCookieAttrs = () => ({
  path: '/',
  sameSite: 'Lax',
  secure: window.location.protocol === 'https:',
});

Cookies.defaults = {
  ...Cookies.defaults,
  ...sessionCookieAttrs(),
};

export const getLoadingStatus = state => state.fetchAPIloadingStatus;
export const setLoadingStatus = (state, status) => {
  state.fetchAPIloadingStatus = status;
};

export const setUser = user => {
  emitter.emit(CHATWOOT_SET_USER, { user });
  emitter.emit(ANALYTICS_IDENTITY, { user });
};

const readAuthHeader = (headers, name) => {
  if (!headers) return undefined;
  if (typeof headers.get === 'function') {
    return headers.get(name) || headers.get(name.toLowerCase()) || undefined;
  }
  return headers[name] || headers[name.toLowerCase()];
};

export const serializeAuthHeaders = headers => {
  const authData = {};
  AUTH_HEADER_KEYS.forEach(key => {
    const value = readAuthHeader(headers, key);
    if (value != null && value !== '') {
      authData[key] = value;
    }
  });
  if (!authData['token-type']) {
    authData['token-type'] = 'Bearer';
  }
  return authData;
};

export const getHeaderExpiry = response => {
  const expiryUnix = Number(readAuthHeader(response?.headers, 'expiry'));
  if (Number.isFinite(expiryUnix) && expiryUnix > 0) {
    return fromUnixTime(expiryUnix);
  }
  return new Date(Date.now() + 14 * 864e5);
};

export const purgePathScopedSessionCookies = () => {
  SESSION_COOKIE_KEYS.forEach(name => {
    STALE_SESSION_COOKIE_PATHS.forEach(path => {
      Cookies.remove(name, { path });
    });
  });
};

export const setAuthCredentials = response => {
  const authData = serializeAuthHeaders(response.headers);
  const expiryDate = getHeaderExpiry(response);
  let days = differenceInDays(expiryDate, new Date());
  if (!Number.isFinite(days) || days < 1) {
    days = 14;
  }
  purgePathScopedSessionCookies();
  Cookies.set('cw_d_session_info', JSON.stringify(authData), {
    ...sessionCookieAttrs(),
    expires: days,
  });
  setUser(response.data.data, expiryDate);
};

export const clearBrowserSessionCookies = () => {
  purgePathScopedSessionCookies();
  SESSION_COOKIE_KEYS.forEach(name => {
    Cookies.remove(name, { path: '/' });
    Cookies.remove(name);
  });
};

export const clearLocalStorageOnLogout = () => {
  LocalStorage.remove(LOCAL_STORAGE_KEYS.DRAFT_MESSAGES);
};

export const clearSessionStorageOnLogout = () => {
  SessionStorage.remove(SESSION_STORAGE_KEYS.IMPERSONATION_USER);
};

export const deleteIndexedDBOnLogout = async () => {
  let dbs = [];
  try {
    dbs = await window.indexedDB.databases();
    dbs = dbs.map(db => db.name);
  } catch (e) {
    dbs = JSON.parse(localStorage.getItem('cw-idb-names') || '[]');
  }

  dbs.forEach(dbName => {
    const deleteRequest = window.indexedDB.deleteDatabase(dbName);

    deleteRequest.onerror = event => {
      // eslint-disable-next-line no-console
      console.error(`Error deleting database ${dbName}.`, event);
    };

    deleteRequest.onsuccess = () => {
      // eslint-disable-next-line no-console
      console.log(`Database ${dbName} deleted successfully.`);
    };
  });

  localStorage.removeItem('cw-idb-names');
};

export const clearCookiesOnLogout = ({ sessionExpired } = {}) => {
  emitter.emit(CHATWOOT_RESET);
  emitter.emit(ANALYTICS_RESET);
  const lastAuthGate = SessionStorage.get(SESSION_STORAGE_KEYS.LAST_AUTH_GATE);
  clearBrowserSessionCookies();
  clearLocalStorageOnLogout();
  clearSessionStorageOnLogout();
  SessionStorage.remove(SESSION_STORAGE_KEYS.LAST_AUTH_GATE);
  SessionStorage.remove(SESSION_STORAGE_KEYS.AGENT_LOGIN_GATE);
  const globalConfig = window.globalConfig || {};
  const logoutRedirectLink = isLocalAuthPreview()
    ? '/app/login'
    : globalConfig.LOGOUT_REDIRECT_LINK || '/auth.html?mode=login';
  if (lastAuthGate === 'agent') {
    if (isLocalAuthPreview()) {
      window.location = '/app/login/agent';
      return;
    }
    window.location = sessionExpired
      ? '/auth.html?mode=agent&error=session'
      : '/auth.html?mode=agent';
    return;
  }
  window.location = logoutRedirectLink;
};

export const parseAPIErrorResponse = error => {
  if (error?.response?.data?.message) {
    return error?.response?.data?.message;
  }
  if (error?.response?.data?.error) {
    return error?.response?.data?.error;
  }
  if (error?.response?.data?.errors) {
    return error?.response?.data?.errors[0];
  }
  return error;
};

export const throwErrorMessage = error => {
  const errorMessage = parseAPIErrorResponse(error);
  throw new Error(errorMessage);
};

export const parseLinearAPIErrorResponse = (error, defaultMessage) => {
  const errorData = error.response.data;
  const errorMessage = errorData?.error?.errors?.[0]?.message || defaultMessage;
  return errorMessage;
};
